import React, { useEffect, useState, useMemo, useCallback, useRef  } from "react";
import { useAuth } from '../utils/AuthContext';
import { useNavigate, useLocation, Link} from 'react-router-dom';
import { groupMessages } from "../utils//contactFormUtils";
import { MailCheck, ArchiveRestore, ArchiveX } from "lucide-react";
import { useParams } from "react-router-dom";
import { SidebarProvider } from "../components/SidebarContext";
import Loading from '../components/loading/Loading';
import SidebarToggle from '../components/SidebarToggle';
import Sidebar from '../components/Sidebar';
import axios from "../utils/axios";
import NavbarLog from '../components/NavbarLog';
import Footer from '../components/Footer';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import jalaliday from "jalaliday";
import "dayjs/locale/fa";
import "../css/MessagingPage.css";

export default function DashboardMessages() {
  const { lang } = useParams();
  const { user, loading } = useAuth();
  const [consoleError, setConsoleError] = useState("");
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [reply, setReply] = useState("")
  const [prodId, setProdId] =useState("")
  const [setlang, setLang] = useState("fa")
  const [topicPage, setTopicPage] = useState(1);
  const [messagePage, setMessagePage] = useState(1);
  const [activeTab, setActiveTab] = useState("inbox"); 
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openTabDropdown, setTabOpenDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const location = useLocation();
  const threadEndRef = useRef(null);
  const ref = useRef(null);
  const topicsPerPage = 10;
  const messagesPerPage = 20;

  
  const fetchMessages = useCallback(async (signal) => {
    try {
      let url = "messages/";
      let allMessages = [];
      let totalCount = null;
      let page = 0;
      const maxPages = 20;
      const visited = new Set();
  
      while (url && page < maxPages) {
        if (visited.has(url)) {
          console.warn("Circular pagination detected:", url);
          break;
        }
        visited.add(url);
  
        const response = await axios.get(url, { signal });
        const { results, next, count } = response.data;
  
        if (!results || results.length === 0) break;
  
        allMessages = [...allMessages, ...results];
        totalCount = count;
        page += 1;
  
        if (allMessages.length >= totalCount) break;
  
        url = next;
      }
      
      setMessages(allMessages);

    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError") {
        setConsoleError("Fetch aborted");
      } else {
        setConsoleError("Failed to fetch messages:", err);
      }
    }
  }, []);


  // Auto-fetch on mount
  useEffect(() => {
    const controller = new AbortController();
    fetchMessages(controller.signal);
    setCurrentUser(user.username);
    return () => controller.abort();
  }, [fetchMessages]);


  // Group messages memoized, safe
  const groupedMessages = useMemo(() => groupMessages(messages, user?.id), [messages, user?.id]);
  const group = groupedMessages[selectedTopic]; // object with metadata
  const messagesInGroup = (group?.messages || []).sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
  
  // Count unseen groups
  const unseenCount = useMemo(() => {
    if (!groupedMessages) return 0;
    return Object.values(groupedMessages).filter(g => g.hasUnseen).length;
  }, [groupedMessages]);

  
  // Current group & thread memoized
  const thread = useMemo(
    () => groupedMessages[selectedTopic] || [],
    [groupedMessages, selectedTopic]
  );
  const currentGroup = useMemo(() => groupedMessages[selectedTopic], [groupedMessages, selectedTopic]);
  const currentThread = useMemo(() => currentGroup?.messages || [], [currentGroup]);
  const lastMsg = useMemo(
    () => (thread.length > 0 ? thread[thread.length - 1] : null),
    [thread]
  );


  //const currentThread = groupedMessages?.[selectedTopic]?.messages || [];
  const sortedThread = [...currentThread].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );


  // Filtered topics by activeTab
  const filteredTopics = useMemo(() => {
    if (!groupedMessages) return {};

    return Object.fromEntries(
      Object.entries(groupedMessages).filter(([_, g]) => {
        const status = g.isSender ? g.senders_status : g.receivers_status; 

        switch (activeTab) {
          case "archived":
            return status === "archived";
          case "removed":
            return status === "removed";
          default:
            return status === "active";
        }
      })
    );
  }, [groupedMessages, activeTab]);
  

  // prepare data BEFORE return
  const topicKeys = Object.keys(filteredTopics || {});
  const pagedTopics = topicKeys.slice(
    (topicPage - 1) * topicsPerPage,
    topicPage * topicsPerPage
  );


  const pagedMessages = sortedThread.slice(
    (messagePage - 1) * messagesPerPage,
    messagePage * messagesPerPage
  );


  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentThread]);


  // Redirect if unauthenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate(`/${lang}/login`, { state: { from: location }, replace: true });
    }
  }, [loading, user, navigate, location]);


  // Post a reply
  const handleReply = async (parentId, lastMsg, text = "", prodId = null) => {
    const body = (text || "").trim();
    if (!body || !lastMsg) return;
  
    const group = groupedMessages[selectedTopic];
  
    // Use numeric IDs
    const receiverId =
      lastMsg.sender_id === user.id ? lastMsg.receiver_id : lastMsg.sender_id;
        
    try {
      await axios.post("/messages/", {
        subject: group?.subject,
        product: prodId ?? group?.prodId,
        body,
        parent: parentId,
        receiver: receiverId, 
      });
  
      setReply("");
      fetchMessages();
    } catch (err) {
      setConsoleError("Failed to send reply:", err.response?.data || err.message);
    }
  };
  
  const updateThreadStatus = async (userGroupId, newStatus, isSender) => {
    try {
      await axios.post(
        `/messages/groups/${userGroupId}/status/${isSender ? "sender" : "receiver"}/${newStatus}/`
      );
      fetchMessages();
    } catch (err) {
      setConsoleError("Status update failed:", err);
    }
  };
  
  useEffect(() => {
    function handleClickOutside(e) {
      if (!openDropdown) return;
      const el = document.querySelector(`[data-topic="${openDropdown}"]`);
      if (!el || !el.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);
  
  const handleAction = (newStatus, group) => {
    if (!group || !group.user_group_id) {
      setConsoleError("Missing group or user_group_id", group);
      return;
    }
    updateThreadStatus(
      group.user_group_id,
      newStatus,
      group.isSender 
    );
    setOpen(false); 

  };

  const markMessagesSeen = async (group) => {
    if (!group || !group.messages) return;
  
    for (const msg of group.messages) {
      if (group.isSender && msg.senders_seen === "unseen") {
        await axios.put(
          `/messages/${msg.id}/groups/${group.user_group_id}/seen/sender/`
        );
      } else if (!group.isSender && msg.receivers_seen === "unseen") {
        await axios.put(
          `/messages/${msg.id}/groups/${group.user_group_id}/seen/receiver/`
        );
      }
    }
    fetchMessages(); 
  };

  // Transation
  dayjs.extend(utc);
  dayjs.extend(jalaliday);
  const toPersiDigits = (str) =>
    str.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
  
  useEffect (() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if(lang !== "fa" && lang !== "en") {
      if ( userTimezone === "Asia/Tehran") {
        setLang(`fa`);
      } else {
        setLang(`en`);
      }
    } else {
      setLang(lang)
    }
  }, [lang])  
  
  
  if (loading) return <Loading />;  

  if (!user) {
    return (
      <>
        <NavbarLog />
        <Sidebar />
          {setlang === "en" ? 
          <div className="messaging-page-en">
            You must be logged in to view messages<Link to={`/${lang}/login`}>login here</Link>!
          </div>
          :
          <div className="messaging-page-fa">
          ! برای نمایش پیام‌ها لطفا <Link to={`/${lang}/login`}>از اینجا وارد شو</Link>!
          </div>
          }
        <Footer />
      </>
    );
  }
  

  return (
  <>
    <NavbarLog />
    <SidebarProvider>
    <SidebarToggle setlang={setlang==="en"?"en":"fa"} />
      <div className={setlang==="en"?"messaging-page-en":"messaging-page-fa"}>
        {setlang==="en" && 
          <Sidebar />
        }
        {/* Sidebar with Topics */}
        <div className="messages-list">
          <h2 className="section-title">Mailbox</h2>

          <div className={setlang==="en"?"tabs-container-en":"tabs-container-fa"}>
            <button
              onClick={() => setActiveTab("inbox")}
              className={activeTab === "inbox" ? "active" : ""}
            >
              {setlang === "en" ? "Inbox" : "پیام‌ها"}
            </button>
            <button
              onClick={() => setActiveTab("archived")}
              className={activeTab === "archived" ? "active" : ""}
            >
              {setlang === "en" ? "Archived" : "آرشیو"}
            </button>
            <button
              onClick={() => setActiveTab("removed")}
              className={activeTab === "removed" ? "active" : ""}
            >
              {setlang === "en" ? "Removed" : "حذف"}
            </button>
          </div>
          <div className="tabs-container-mini">
            <button onClick={(e) => {
              e.stopPropagation();
              setTabOpenDropdown(!openTabDropdown)}
            }>
              {activeTab === "inbox" ? <MailCheck size={16} /> :
              activeTab === "archived" ? <ArchiveRestore size={16} /> : 
              activeTab === "removed" && <ArchiveX size={16} />} {activeTab}
            </button>
            {openTabDropdown && (
              <ul className="tab-dropdown">
                <li>
                  <button onClick={() => {setActiveTab("inbox"); setTabOpenDropdown(!openTabDropdown)}} className={activeTab === "inbox" ? "active" : ""}>
                    {setlang === "en" ? "Inbox" : "پیام‌ها"}
                  </button>
                </li>
                <li>
                  <button onClick={() => {setActiveTab("archived"); setTabOpenDropdown(!openTabDropdown)}} className={activeTab === "archived" ? "active" : ""}>
                    {setlang === "en" ? "Archived" : "آرشیو"}
                  </button>
                </li>
                <li>
                  <button onClick={() => {setActiveTab("removed"); setTabOpenDropdown(!openTabDropdown)}} className={activeTab === "removed" ? "active" : ""}>
                    {setlang === "en" ? "Removed" : "حذف"}
                  </button>
                </li>
              </ul>
            )}
          </div>

            <ul className="topics-list">
              {pagedTopics.map((topicKey) => {
                const group = groupedMessages[topicKey];
                const firstMsg = group?.messages?.at(-1);

                return (
                  <li
                    key={topicKey}
                    data-topic={topicKey}
                    onClick={(e) => {
                      if (e.target.closest(".dropdown-menu")) return;
                      setSelectedTopic(topicKey);
                      markMessagesSeen(group); 
                    }}
                    className={`message-item topic-card 
                      ${selectedTopic === topicKey ? "selected" : ""} 
                      ${group.hasUnseen ? "unseen" : ""}`}
                  >
                    <button  className="dash-button" onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(openDropdown === topicKey ? null : topicKey)}}>
                    ⋮⋮
                    </button>
                    <div className="topic-card" 
                    onClick={(e) => e.stopPropagation()}>
                      <div className="thread-actions" 
                      >
                      {openDropdown === topicKey  && (
                          <ul className="dropdown-menu">
                            <li>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAction(group.status === "archived" ? "active" : "archived",
                                    group
                                  );
                                  setOpenDropdown(null); 
                                }}
                              >
                              {setlang === "en" ? (
                                <>
                                {group.status === "archived" ? "Unarchive" : "Archive"}
                                </>
                              ) : (
                                <>
                                {group.status === "archived" ? "بازیافت" : "آرشیو"}
                                </>
                              )}
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAction(group.status === "removed" ? "active" : "removed",
                                    group
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                              {setlang === "en" ? (
                                <>
                                {group.status === "removed" ? "Restore" : "Remove"} 
                                </>
                              ):(
                                <>
                                {group.status === "removed" ? "بازیافت" : "حذف"}
                                </>
                              )}

                              </button>
                            </li>
                            {group.status === "removed" && (
                              <li>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAction("hzf", group);
                                    setOpenDropdown(null);
                                  }}
                                >
                                  {setlang === "en" ? "Delete": "حذف کامل"}
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>

                    {group.product_image ? (
                      <img onClick={(e) => {
                      if (e.target.closest(".dropdown-menu")) return;
                      setSelectedTopic(topicKey);
                      markMessagesSeen(group); 
                    }}
                    src={group.product_image} alt="Product" className="topic-product-img" />
                    ) : (
                      <div onClick={(e) => {
                      if (e.target.closest(".dropdown-menu")) return;
                      setSelectedTopic(topicKey);
                      markMessagesSeen(group); 
                    }}
                    className="product-placeholder">§</div>
                    )}

                      <div className="topic-info"
                      onClick={(e) => {
                      if (e.target.closest(".dropdown-menu")) return;
                      setSelectedTopic(topicKey);
                      markMessagesSeen(group); 
                    }}>
                        <span className="subject">
                        {group.subject}
                        {group.seen === "unseen" && <span className="unread-badge">•</span>} 
                        </span>
                        {firstMsg && (
                          <span className="timestamp">
                            {dayjs.utc(firstMsg.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm")}
                          </span>
                        )}
                        <div className="preview">
                          {firstMsg ? `${firstMsg.body.slice(0, 40)}...` : "No messages"}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
        </div>

        {/* Thread View */}
        <div className="message-detail">
          {selectedTopic ? (
            <>
              <div className="thread">
                <h3 className="thread-title">
                  {groupedMessages[selectedTopic]?.subject}
                </h3>

                {(groupedMessages[selectedTopic]?.messages || [])
                  .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                  .map((msg) => {
                    const isSender = msg.sender_username === currentUser;

                    return (
                      <div
                        key={msg.id}
                        className={`message-thread-item ${isSender ? "sender" : "receiver"}`}
                      >
                        {/* Profile */}
                        <div className="message-profile">
                          {msg.profile_picture ? (
                            <img
                              src={msg.profile_picture}
                              alt={msg.sender_username}
                              className="profile-img"
                            />
                          ) : (
                            <div className="profile-placeholder"> 👤 </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="message-content">
                          <p className="from">
                            <strong>{msg.sender_username}</strong>
                          </p>
                          <div className="message-body">{msg.body}</div>
                          <p className="timestamp">
                            {dayjs.utc(msg.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm")}
                          </p>
                        {/*<div ref={bottomRef} />*/}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Reply Box */}
              <div className="reply-box">
                <textarea
                  className={setlang === "en" ? "reply-input" : "reply-input-fa"}
                  placeholder={setlang === "en" ? "Write your reply...": "لطفا پیام خود را وارد کنید" }
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />

                <button
                  onClick={(e) => {
                    e.preventDefault();
                    const group = groupedMessages[selectedTopic];
                    const thread = group?.messages || [];
                    const lastMsg = thread[thread.length - 1];
                    if (lastMsg) {
                      handleReply(lastMsg.id, lastMsg, reply, group?.prodId)
                    }
                  }}
                  className="reply-button"
                  disabled={
                    !(
                      groupedMessages[selectedTopic]?.messages?.length > 0 &&
                      reply?.trim() !== ""
                    )
                  }
                >
                  {setlang === "en" ? "Send Reply" : "ارسال" }
                </button>
              </div>
            </>
          ) : (
            <div>
            {setlang === "en" ? 
              <p className="empty-state">Select a topic to read</p> :
              <p className="empty-state">پیام خود را انتخاب کنید</p> 
              }
            </div>
          )}
        </div>
        {setlang==="fa" && 
          <Sidebar />
        }
      </div>
      </SidebarProvider>
    <Footer />
  </>
  );
}